import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { apiKeysAdminApi } from '../../../services/admin/apiKeysAdminApi'
import type { ApiKeyCreateResponse, ApiKeyPermission } from '../../../types/apiKeys'
import AccountPicker from './AccountPicker'
import IssuedKeyReveal from './IssuedKeyReveal'

const EXPIRY_CHOICES = [30, 90, 180, 365]

/**
 * The API's own error envelope, which the axios interceptor preserves under
 * `details` for statuses it has no special handling for. Read it so a 409 can
 * be told apart from any other failure rather than surfacing as "HTTP 409".
 */
function serverError(err: unknown): { code?: string; message?: string } {
  const details = (err as { details?: unknown })?.details
  const error = (details as { error?: { code?: string; message?: string } })?.error
  return error ?? {}
}

/**
 * Admin issuance, including the perpetual-key escape hatch.
 *
 * Perpetual is deliberately awkward: it requires a written justification and
 * shows a warning, because a key with no expiry has no automatic containment if
 * it leaks — revocation becomes the only control, and that depends on someone
 * noticing. The friction is the point.
 */
export default function IssueKeyDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void
  onIssued: () => void
}) {
  const [email, setEmail] = useState('')
  const [label, setLabel] = useState('')
  const [permission, setPermission] = useState<ApiKeyPermission>('read_write')
  const [neverExpires, setNeverExpires] = useState(false)
  const [expiryDays, setExpiryDays] = useState(90)
  const [justification, setJustification] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<ApiKeyCreateResponse | null>(null)

  // Offered only after the server has actually refused on the limit. Showing an
  // override before anyone has hit the cap invites using it by default, which
  // would leave the cap doing nothing.
  const [limitReached, setLimitReached] = useState(false)
  const [overrideLimit, setOverrideLimit] = useState(false)

  const canSubmit =
    email.trim().length > 0 &&
    (!neverExpires || justification.trim().length > 0) &&
    (!limitReached || overrideLimit)

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const result = await apiKeysAdminApi.create({
        email: email.trim(),
        label: label.trim() || undefined,
        permission,
        never_expires: neverExpires || undefined,
        expires_in_days: neverExpires ? undefined : expiryDays,
        justification: neverExpires ? justification.trim() : undefined,
        override_limit: overrideLimit || undefined,
      })
      setCreated(result)
      onIssued()
    } catch (err) {
      const { code, message } = serverError(err)
      if (code === 'KEY_LIMIT_REACHED') {
        setLimitReached(true)
      }
      setError(message ?? (err instanceof Error ? err.message : 'Failed to issue API key'))
    } finally {
      setSubmitting(false)
    }
  }

  // Once issued, the dialog becomes the show-once surface — the admin has to
  // deliver this value to the account owner, and it is never retrievable again.
  if (created) {
    return <IssuedKeyReveal created={created} onClose={onClose} recipient={email.trim()} />
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 p-5">
          <h2 className="text-lg font-semibold text-gray-900">Issue an API key</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {limitReached && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3">
              <label className="flex items-start gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={overrideLimit}
                  onChange={(e) => setOverrideLimit(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-amber-300"
                />
                <span>
                  Issue anyway, over the limit. Every active key on this account counts
                  toward the same cap the owner is held to, so one more here is one fewer
                  they can generate for themselves. Prefer revoking a stale key first.
                </span>
              </label>
            </div>
          )}

          <label className="block text-sm text-gray-700">
            Account email
            <AccountPicker
              value={email}
              onChange={(next) => {
                setEmail(next)
                // The limit belongs to the account, not the form — a different
                // target has its own count, and carrying the override across
                // would apply a decision made about someone else.
                setLimitReached(false)
                setOverrideLimit(false)
              }}
              autoFocus
            />
            <span className="mt-1 block text-xs text-gray-500">
              The key acts as this account and reaches only what they can see — issuing it
              as an admin does not give it admin reach.
            </span>
          </label>

          <label className="block text-sm text-gray-700">
            Label
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={64}
              placeholder="e.g. Nightly export"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="block text-sm text-gray-700">
            Permissions
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value as ApiKeyPermission)}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="read_write">Read &amp; write</option>
              <option value="read_only">Read only</option>
            </select>
            <span className="mt-1 block text-xs text-gray-500">
              What the key can do. It does not widen reach.
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={neverExpires}
              onChange={(e) => setNeverExpires(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300"
            />
            <span>Never expires</span>
          </label>

          {!neverExpires && (
            <label className="block text-sm text-gray-700">
              Expires in
              <select
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                {EXPIRY_CHOICES.map((days) => (
                  <option key={days} value={days}>
                    {days} days
                  </option>
                ))}
              </select>
            </label>
          )}

          {neverExpires && (
            <div className="space-y-3 rounded border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm text-amber-900">
                A perpetual key has no automatic containment — if it leaks, revoking it
                by hand is the only remedy. Use it only where rotation is genuinely
                impossible.
              </p>
              <label className="block text-sm text-amber-900">
                Justification (recorded on the key)
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={2}
                  placeholder="e.g. embedded logger with no update path"
                  className="mt-1 w-full rounded border border-amber-300 px-3 py-2 text-sm"
                />
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 p-5">
          <button
            onClick={onClose}
            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="rounded bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {submitting ? 'Issuing…' : 'Issue key'}
          </button>
        </div>
      </div>
    </div>
  )
}
